// src/services/connectionService.js
import { db, auth } from "../firebase/config";
import { onAuthStateChanged } from "firebase/auth";

// A session entry is refreshed on this cadence; the admin reader treats entries
// whose lastSeen is older than STALE as gone (covers crashed tabs / killed dev
// servers whose onDisconnect never fired — the old push()-per-connection model
// left those as permanent ghosts, inflating the count indefinitely).
const SESSION_HEARTBEAT_MS = 45 * 1000;
const SESSION_STALE_MS = 3 * 60 * 1000;

// Visitor geolocation (admin visits map). Resolved server-side from the
// CloudFront viewer headers so the browser makes ONE call to our own API
// instead of a third-party geo host; ipify stays as the IP-only fallback.
const GEO_API_URL = "https://api.neuralward.com/auth/geo";
// Per-TAB cache of the resolved address + geo. sessionStorage, not local: the
// address can change between browser sessions, but never within one tab.
const GEO_CACHE_KEY = "_nw_geo";
// Two meanings, both 7, deliberately the same number:
//   · how far the map's "< [date] >" pager reaches back in CALENDAR days, and
//   · how many DATES VISITED each account keeps, however old those dates are.
// Retention is per-account, not calendar-based — see geoVisitsService.
export const VISIT_HISTORY_DAYS = 7;

// Local calendar day key — the admin reading the map and the visitors being
// plotted are on the same wall clock as far as the "< [date] >" control cares.
export function visitDayKey(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

class ConnectionService {
  constructor() {
    this.isOnline = navigator.onLine;
    this.firestoreEnabled = true;
    this.realtimeDbEnabled = true;
    this.connectionListeners = new Set();
    this.initialized = false;

    // Realtime Database refs for this client's own session entry.
    this.mySessionRef = null;
    this.sessionOnDisconnectArmed = false;
    this.heartbeatTimer = null;
    this.totalClients = 0;
    this.database = null;

    // Identity + network context attached to this client's session entry.
    this.clientIp = "";
    this.clientIpPromise = null;
    this.geo = null; // { country, countryName, region, city, lat, lon }
    this.visitLogged = false;
    this.ipBanChecked = false;
    this.ipBanListeners = new Set();
    this.myVisitRef = null; // today's history row, refreshed by the heartbeat
    // Journey = one child per page this tab stayed on, {path, from, to}.
    this.journeyKey = null; // key of the segment currently being extended
    this.journeyPath = null;
    this.identity = { registered: false, uid: null, name: null, email: null };
    this.rtdb = null; // cached firebase/database module
  }

  /**
   * Initialize connection monitoring and per-client session tracking.
   * Call this after Firebase is ready (gated on first user gesture in App.js).
   */
  async initialize() {
    if (this.initialized) return;

    try {
      const rtdb = await import("firebase/database");
      this.rtdb = rtdb;
      const { getDatabase, ref, onValue } = rtdb;
      this.database = getDatabase();

      const sid = this._getSessionId();
      this.mySessionRef = ref(this.database, `active_sessions_list/${sid}`);
      const connectionStatusRef = ref(this.database, ".info/connected");

      // Kick off IP resolution (same source the chat uses) and identity.
      this._fetchClientIp();
      this.identity = this._readIdentity();
      onAuthStateChanged(auth, () => {
        this.identity = this._readIdentity();
        // Login/logout while connected: refresh this tab's entry in place.
        this._writeSession();
        // ...and the history row, which was written once when geo resolved.
        // Firebase restores the session asynchronously, so on a direct load of
        // an authed route (/admin) that write usually lands BEFORE currentUser
        // exists — leaving the visitor permanently anonymous on the map for the
        // rest of the day. Re-stamping identity here is what fixes that.
        this._writeVisitIdentity();
        this._writeLastSeen();
      });

      window.addEventListener("online", this.handleOnline.bind(this));
      window.addEventListener("offline", this.handleOffline.bind(this));

      onValue(
        connectionStatusRef,
        (snapshot) => {
          const connected = snapshot.val();
          if (connected) {
            this.notifyListeners({ type: "realtime-db", connected: true });
            this._armSessionOnDisconnect();
            this._writeSession();
            this._startHeartbeat();
          } else {
            this.notifyListeners({ type: "realtime-db", connected: false });
            // Don't cancel the onDisconnect handler — on a real drop the server
            // fires it and removes our entry. Just stop refreshing locally.
            this._stopHeartbeat();
          }
        },
        (error) => {
          console.error("Error monitoring Realtime Database connection:", error);
        }
      );

      this.initialized = true;
    } catch (error) {
      console.error("Error initializing connection service:", error);
    }
  }

  _getSessionId() {
    // Per-tab id (shared with presenceService's Monthly Visits sid). Stable
    // across reloads within a tab → the entry is overwritten, not duplicated.
    let sid = sessionStorage.getItem("_nw_sid");
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem("_nw_sid", sid);
    }
    return `s_${sid}`;
  }

  _readIdentity() {
    const user = auth.currentUser;
    // Anonymous Firebase users (presence sign-in) are NOT registered accounts.
    const registered = !!(user && !user.isAnonymous);
    return {
      registered,
      uid: registered ? user.uid : null,
      name: registered ? user.displayName || null : null,
      email: registered ? user.email || null : null,
    };
  }

  /** Everything that has to happen once the address is known. */
  _onIpResolved() {
    this._writeSession();
    this._logVisit();
    this._checkIpBan();
    return this.clientIp;
  }

  _fetchClientIp() {
    if (this.clientIp) return Promise.resolve(this.clientIp);
    if (this.clientIpPromise) return this.clientIpPromise;

    // A reload re-enters this with the same tab. The address and its geo cannot
    // meaningfully change while the tab lives, so reuse the cached answer rather
    // than calling the lambda again — in dev, where every recompile reloads the
    // page, that alone was most of the traffic to /auth/geo.
    // Deferred to a microtask so `initialize()` finishes setting `identity`
    // first; resolving synchronously here would log the visit as anonymous.
    const cached = this._readCachedGeo();
    if (cached) {
      this.clientIpPromise = Promise.resolve().then(() => {
        this.clientIp = cached.ip;
        this.geo = cached.geo || null;
        return this._onIpResolved();
      });
      return this.clientIpPromise;
    }

    this.clientIpPromise = fetch(GEO_API_URL)
      .then((r) => r.json())
      .then((d) => {
        this.clientIp = d.ip || "";
        if (d.country) {
          this.geo = {
            country: d.country,
            countryName: d.countryName || d.country,
            region: d.region || "",
            city: d.city || "",
            lat: typeof d.lat === "number" ? d.lat : null,
            lon: typeof d.lon === "number" ? d.lon : null,
          };
        }
        this._cacheGeo();
        // Once IP + geo land, stamp them onto the live entry and record the
        // visit in the rolling history the admin map pages through.
        return this._onIpResolved();
      })
      .catch(() =>
        // Geo endpoint unreachable — fall back to the IP alone so the Active
        // Sessions tracker (which dedups guests by IP) never regresses.
        fetch("https://api.ipify.org?format=json")
          .then((r) => r.json())
          .then((d) => {
            this.clientIp = d.ip || "";
            this._cacheGeo();
            return this._onIpResolved();
          })
          .catch(() => {
            this.clientIp = "";
            return "";
          })
      );
    return this.clientIpPromise;
  }

  _readCachedGeo() {
    try {
      const raw = sessionStorage.getItem(GEO_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && parsed.ip ? parsed : null;
    } catch {
      return null;
    }
  }

  _cacheGeo() {
    if (!this.clientIp) return;
    try {
      sessionStorage.setItem(
        GEO_CACHE_KEY,
        JSON.stringify({ ip: this.clientIp, geo: this.geo })
      );
    } catch {
      // Private mode / storage full — just means the next reload re-fetches.
    }
  }

  /**
   * Notify when THIS visitor's IP is on the admin ban list.
   * Registered here rather than at the app root because the check needs the IP
   * we resolve anyway, and that resolution is already gated on first
   * interaction — so a crawler never triggers the lookup.
   */
  onIpBanned(callback) {
    this.ipBanListeners.add(callback);
    return () => this.ipBanListeners.delete(callback);
  }

  async _checkIpBan() {
    if (this.ipBanChecked || !this.clientIp) return;
    this.ipBanChecked = true;
    const { isIpBanned } = await import("./ipBanService");
    if (await isIpBanned(this.clientIp)) {
      this.ipBanListeners.forEach((cb) => {
        try {
          cb(this.clientIp);
        } catch (e) {
          console.error("ConnectionService: ip-ban listener failed", e);
        }
      });
    }
  }

  /**
   * Append this tab to the rolling visit history (`visit_geo/<day>/<sid>`).
   * One entry per tab per day, keyed the same way as the live session entry so
   * a reload overwrites instead of duplicating. Applies to EVERY visitor —
   * registered or not.
   *
   * **A RELOAD MUST NOT `set()` THIS ROW.** The sid survives a reload, so a
   * plain `set()` re-enters the same path and (a) resets `at` to now, throwing
   * away however long the tab had already been open, and (b) DELETES `journey`,
   * throwing away every page visited before the reload. A tab open since
   * midday then refreshed reported minutes instead of hours, and its page
   * history restarted from empty. `at` and `journey` are write-once per tab per
   * day; everything else is refreshed.
   *
   * We can't `get()` the row to decide — `visit_geo` is admin-read-only, so a
   * normal visitor is denied. sessionStorage has exactly the right lifetime
   * (same tab, cleared with the sid), so it carries the marker instead.
   */
  _logVisit() {
    if (this.visitLogged || !this.database || !this.rtdb) return;
    if (!this.clientIp) return;
    this.visitLogged = true;

    const { ref, set, update, serverTimestamp } = this.rtdb;
    const sid = this._getSessionId();
    const dayKey = visitDayKey();
    const entry = ref(this.database, `visit_geo/${dayKey}/${sid}`);
    this.myVisitRef = entry;

    // Day-scoped: crossing midnight in an open tab starts a fresh row, as it
    // should, because the row itself is keyed by day.
    const createdKey = `_nw_visit_${dayKey}`;
    const alreadyCreated = sessionStorage.getItem(createdKey) === "1";

    const mutable = {
      ip: this.clientIp,
      country: this.geo?.country || "",
      countryName: this.geo?.countryName || "",
      region: this.geo?.region || "",
      city: this.geo?.city || "",
      lat: this.geo?.lat ?? null,
      lon: this.geo?.lon ?? null,
      registered: this.identity.registered,
      uid: this.identity.uid || null,
      name: this.identity.name || null,
      email: this.identity.email || null,
      // `lastSeen` rides the heartbeat; paired with `at` it lets the map show a
      // visit as a SPAN ("11:22 – 13:35") instead of a single instant.
      lastSeen: serverTimestamp(),
    };

    const write = alreadyCreated
      ? update(entry, mutable)
      : set(entry, { ...mutable, at: serverTimestamp() });

    write
      .then(() => {
        sessionStorage.setItem(createdKey, "1");
        this._startJourneySegment(window.location.pathname);
        this._writeLastSeen();
      })
      .catch((err) => {
        // A missing RTDB rule for visit_geo lands here — the live session
        // tracker is unaffected, only the map's history stops filling.
        this.myVisitRef = null;
        this.visitLogged = false;
        console.error("ConnectionService: failed to log visit", err);
      });
  }

  /**
   * Re-stamp identity onto today's history row after auth resolves.
   *
   * `_logVisit` runs once, when geo lands, and `visitLogged` stops it running
   * again — so without this the row keeps whatever identity existed at that
   * instant. Only ever upgrades anonymous → registered: a sign-OUT must not
   * erase who was already recorded as visiting.
   */
  _writeVisitIdentity() {
    if (!this.myVisitRef || !this.rtdb || !this.identity.registered) return;
    const { update } = this.rtdb;
    update(this.myVisitRef, {
      registered: true,
      uid: this.identity.uid || null,
      name: this.identity.name || null,
      email: this.identity.email || null,
    }).catch(() => {});
  }

  /**
   * Durable per-account presence: `last_seen/{uid}` = { at, ip, page }.
   *
   * This is the ONLY record of "when was this user last on the portal" that
   * covers every visitor. `online/{uid}` (presenceService) doesn't: it lives in
   * lazy admin/store chunks, so someone reading marketing pages never writes it.
   * `visit_geo` doesn't either — it is keyed by day/sid and pruned at 7 days.
   *
   * Written on the same 45s heartbeat tick as everything else, but it is a
   * SEPARATE `update()` — it targets a different top-level node, so unlike the
   * journey patch it cannot ride the session write's multi-path merge. RTDB
   * bills bandwidth and storage rather than operations, so the extra call is
   * immaterial; do not "optimise" it into the session update, which would put
   * presence data under `active_sessions_list` where it is deleted on
   * disconnect — the exact opposite of what a durable last-seen record needs.
   */
  _writeLastSeen() {
    if (!this.database || !this.rtdb || !this.identity.registered) return;
    if (!this.identity.uid) return;
    const { ref, update, serverTimestamp } = this.rtdb;
    update(ref(this.database, `last_seen/${this.identity.uid}`), {
      at: serverTimestamp(),
      ip: this.clientIp || "",
      page: window.location.pathname,
    }).catch(() => {});
  }

  /**
   * Route change, reported by the router (App.js) rather than sniffed from
   * history — the router is the source of truth, and this avoids monkey-patching
   * pushState, which TruConversion already wraps.
   *
   * No-ops until the visit row exists, so nothing happens before the visitor's
   * first gesture (Home invariant #7).
   */
  notePage(path) {
    if (!this.myVisitRef || !path || path === this.journeyPath) return;
    this._closeJourneySegment();
    this._startJourneySegment(path);
  }

  /**
   * Stamp the leg we're leaving with the exact moment of departure.
   * Without this its `to` would only be as fresh as the last heartbeat, so any
   * page left within 45s would report roughly zero time spent.
   */
  _closeJourneySegment() {
    if (!this.myVisitRef || !this.rtdb || !this.journeyKey) return;
    const { child, update, onDisconnect, serverTimestamp } = this.rtdb;
    const leg = child(this.myVisitRef, `journey/${this.journeyKey}`);
    // Drop the tab-closed handler first: this leg now has a real end time, and
    // leaving it armed would later overwrite that with the disconnect time.
    onDisconnect(leg).cancel().catch(() => {});
    update(leg, { to: serverTimestamp() }).catch(() => {});
    this.journeyKey = null;
    this.journeyPath = null;
  }

  _startJourneySegment(path) {
    if (!this.myVisitRef || !this.rtdb) return;
    const { push, child, set, onDisconnect, serverTimestamp } = this.rtdb;
    // push() keys are unique and sort chronologically. An index would restart
    // at 0 after a reload and overwrite the earlier legs, because the sid — and
    // therefore this row — survives a reload.
    const segment = push(child(this.myVisitRef, "journey"));
    this.journeyKey = segment.key;
    this.journeyPath = path;
    set(segment, {
      path: String(path).slice(0, 200),
      from: serverTimestamp(),
      to: serverTimestamp(),
    })
      .then(() => {
        // The LAST page of a visit never gets a navigation to close it, so the
        // server stamps its end when the tab goes away. Without this the final
        // page — often the interesting one — would always look 45s short.
        onDisconnect(segment)
          .update({ to: serverTimestamp() })
          .catch(() => {});
      })
      .catch(() => {
        this.journeyKey = null;
        this.journeyPath = null;
      });
  }

  _armSessionOnDisconnect() {
    if (this.sessionOnDisconnectArmed || !this.mySessionRef || !this.rtdb) return;
    const { onDisconnect } = this.rtdb;
    onDisconnect(this.mySessionRef)
      .remove()
      .then(() => {
        this.sessionOnDisconnectArmed = true;
      })
      .catch((err) => {
        console.error("ConnectionService: failed to arm session onDisconnect", err);
      });
  }

  _writeSession() {
    if (!this.mySessionRef || !this.rtdb) return;
    const { set, serverTimestamp } = this.rtdb;
    const payload = {
      ip: this.clientIp || "",
      country: this.geo?.country || "",
      region: this.geo?.region || "",
      city: this.geo?.city || "",
      lat: this.geo?.lat ?? null,
      lon: this.geo?.lon ?? null,
      registered: this.identity.registered,
      uid: this.identity.uid || null,
      name: this.identity.name || null,
      email: this.identity.email || null,
      page: window.location.pathname,
      lastSeen: serverTimestamp(),
    };
    set(this.mySessionRef, payload).catch((err) => {
      console.error("ConnectionService: failed to write session", err);
    });
  }

  _startHeartbeat() {
    if (this.heartbeatTimer) return;
    this.heartbeatTimer = setInterval(() => {
      if (!this.mySessionRef || !this.rtdb) return;
      const { update, serverTimestamp } = this.rtdb;
      update(this.mySessionRef, {
        lastSeen: serverTimestamp(),
        page: window.location.pathname,
      }).catch(() => {});
      // Extend today's history row so the visit reads as a duration, and close
      // out the current journey leg in the SAME multi-path update — RTDB merges
      // slash-paths into one write, so this stays a single request per beat.
      if (this.myVisitRef) {
        const patch = { lastSeen: serverTimestamp() };
        if (this.journeyKey) {
          patch[`journey/${this.journeyKey}/to`] = serverTimestamp();
        }
        update(this.myVisitRef, patch).catch(() => {});
      }
      this._writeLastSeen();
    }, SESSION_HEARTBEAT_MS);
  }

  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Admin-only: subscribe to the live set of active sessions.
   * Filters out stale/ghost entries (no fresh lastSeen), then dedups registered
   * accounts by uid and anonymous visitors by IP. The callback receives
   * { count, sessions, registeredCount, guestCount }.
   */
  async subscribeToSessions(callback) {
    const { getDatabase, ref, onValue } = await import("firebase/database");
    const database = this.database || getDatabase();
    const sessionsRef = ref(database, "active_sessions_list");

    return onValue(
      sessionsRef,
      (snapshot) => {
        const raw = snapshot.val() || {};
        const now = Date.now();
        const registeredMap = new Map();
        const guestMap = new Map();

        Object.entries(raw).forEach(([key, entry]) => {
          // Legacy ghosts were stored as the literal `true`; ignore anything
          // that isn't a fresh, timestamped object.
          if (!entry || typeof entry !== "object") return;
          if (
            typeof entry.lastSeen !== "number" ||
            entry.lastSeen < now - SESSION_STALE_MS
          ) {
            return;
          }

          if (entry.registered && entry.uid) {
            const existing = registeredMap.get(entry.uid);
            if (!existing || entry.lastSeen > existing.lastSeen) {
              registeredMap.set(entry.uid, { ...entry, key });
            }
          } else {
            const ipKey = entry.ip || `unknown_${key}`;
            const g = guestMap.get(ipKey) || {
              ip: entry.ip || "",
              page: entry.page || "",
              lastSeen: 0,
              count: 0,
              country: "",
              region: "",
              city: "",
              lat: null,
              lon: null,
            };
            g.count += 1;
            if (entry.lastSeen > g.lastSeen) {
              g.lastSeen = entry.lastSeen;
              g.page = entry.page || "";
              g.country = entry.country || "";
              g.region = entry.region || "";
              g.city = entry.city || "";
              g.lat = typeof entry.lat === "number" ? entry.lat : null;
              g.lon = typeof entry.lon === "number" ? entry.lon : null;
            }
            guestMap.set(ipKey, g);
          }
        });

        const sessions = [
          ...Array.from(registeredMap.values()).map((s) => ({
            kind: "registered",
            uid: s.uid,
            ip: s.ip || "",
            name: s.name || null,
            email: s.email || null,
            page: s.page || "",
            lastSeen: s.lastSeen,
            count: 1,
            country: s.country || "",
            region: s.region || "",
            city: s.city || "",
            lat: typeof s.lat === "number" ? s.lat : null,
            lon: typeof s.lon === "number" ? s.lon : null,
          })),
          ...Array.from(guestMap.values()).map((g) => ({
            kind: "guest",
            uid: null,
            ip: g.ip || "",
            name: null,
            email: null,
            page: g.page || "",
            lastSeen: g.lastSeen,
            count: g.count,
            country: g.country || "",
            region: g.region || "",
            city: g.city || "",
            lat: typeof g.lat === "number" ? g.lat : null,
            lon: typeof g.lon === "number" ? g.lon : null,
          })),
        ];

        this.totalClients = sessions.length;
        callback({
          count: sessions.length,
          sessions,
          registeredCount: registeredMap.size,
          guestCount: guestMap.size,
        });
      },
      (error) => {
        console.error("ConnectionService: error subscribing to sessions:", error);
        callback({ count: 0, sessions: [], registeredCount: 0, guestCount: 0 });
      }
    );
  }

  async handleOnline() {
    this.isOnline = true;
    await this.updateConnectionState(true);
    this.notifyListeners({ type: "browser", connected: true });
  }

  async handleOffline() {
    this.isOnline = false;
    await this.updateConnectionState(false);
    this.notifyListeners({ type: "browser", connected: false });
  }

  async updateConnectionState(isOnline) {
    try {
      const { enableNetwork, disableNetwork } = await import("firebase/firestore");

      if (isOnline) {
        if (!this.firestoreEnabled) {
          await enableNetwork(db);
          this.firestoreEnabled = true;
        }
        if (this.database && !this.realtimeDbEnabled) {
          const { goOnline } = await import("firebase/database");
          goOnline(this.database);
          this.realtimeDbEnabled = true;
        }
      } else {
        if (this.firestoreEnabled) {
          await disableNetwork(db);
          this.firestoreEnabled = false;
        }
        if (this.database && this.realtimeDbEnabled) {
          const { goOffline } = await import("firebase/database");
          goOffline(this.database);
          this.realtimeDbEnabled = false;
        }
      }
    } catch (error) {
      console.error("Error updating Firebase connection state:", error);
    }
  }

  addConnectionListener(callback) {
    if (typeof callback !== "function") {
      throw new Error("Connection listener must be a function");
    }
    this.connectionListeners.add(callback);
    return () => {
      this.connectionListeners.delete(callback);
    };
  }

  notifyListeners(connectionInfo) {
    this.connectionListeners.forEach((callback) => {
      try {
        callback(connectionInfo);
      } catch (error) {
        console.error("Error in connection listener:", error);
      }
    });
  }

  getConnectionStatus() {
    return {
      browser: this.isOnline,
      firestore: this.firestoreEnabled,
      realtimeDb: this.realtimeDbEnabled,
      initialized: this.initialized,
      totalClients: this.totalClients,
    };
  }

  async forceReconnect() {
    if (!this.initialized) return;
    try {
      await this.updateConnectionState(false);
      setTimeout(async () => {
        await this.updateConnectionState(true);
      }, 1000);
    } catch (error) {
      console.error("Error during forced reconnection:", error);
    }
  }

  cleanup() {
    if (this.initialized) {
      window.removeEventListener("online", this.handleOnline.bind(this));
      window.removeEventListener("offline", this.handleOffline.bind(this));
      this.connectionListeners.clear();
      this._stopHeartbeat();
      this.initialized = false;

      if (this.mySessionRef && this.rtdb) {
        const { onDisconnect, remove } = this.rtdb;
        onDisconnect(this.mySessionRef).cancel().catch(() => {});
        remove(this.mySessionRef).catch(() => {});
        this.mySessionRef = null;
        this.sessionOnDisconnectArmed = false;
      }
    }
  }
}

const connectionService = new ConnectionService();
export default connectionService;
